import React, { useEffect, useState } from "react";
import { FlowPrimaryButton } from "@/components/consultation-flow/ui";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ExternalLink } from "lucide-react";
import type {
  SingleSelectNode as SingleSelectNodeType,
  ClinicalLink,
} from "@/lib/pharmacyFirst/types";

interface SingleSelectNodeProps {
  node: SingleSelectNodeType;
  isDarkMode?: boolean;
  onAnswer: (selectedId: string) => void;
  value?: string | null;
  stepNumber?: number;
  /** One line shown above the options (host-supplied context for this question). */
  preface?: string;
}

function ClinicalLinkChips({
  links,
  isDarkMode,
}: {
  links?: ClinicalLink[];
  isDarkMode?: boolean;
}) {
  if (!links || links.length === 0) return null;
  const styleFor = (ctx?: string) => {
    if (isDarkMode) {
      switch (ctx) {
        case "definition":
          return "border-blue-700 bg-blue-950 text-blue-200 hover:bg-blue-900";
        case "reference":
          return "border-indigo-700 bg-indigo-950 text-indigo-200 hover:bg-indigo-900";
        case "guideline":
          return "border-emerald-700 bg-emerald-950 text-emerald-200 hover:bg-emerald-900";
        case "warning":
          return "border-amber-700 bg-amber-950 text-amber-200 hover:bg-amber-900";
        default:
          return "border-gray-600 bg-gray-800 text-gray-200 hover:bg-gray-700";
      }
    }
    switch (ctx) {
      case "definition":
        return "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100";
      case "reference":
        return "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100";
      case "guideline":
        return "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100";
      case "warning":
        return "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100";
      default:
        return "border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100";
    }
  };
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {links.map((l) => (
        <a
          key={l.url + l.label}
          href={l.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border transition ${styleFor(
            (l as any).context,
          )}`}
        >
          {l.label}
          <ExternalLink className="h-3 w-3" />
        </a>
      ))}
    </div>
  );
}

export { ClinicalLinkChips };

export function SingleSelectNode({
  node,
  isDarkMode = false,
  onAnswer,
  value = null,
  stepNumber,
  preface,
}: SingleSelectNodeProps) {
  const [selected, setSelected] = useState<string | null>(value);

  useEffect(() => {
    setSelected(value || null);
  }, [node.id, value]);

  const canContinue = node.required ? !!selected : true;

  return (
    <div
      className={`border rounded-2xl p-4 ${
        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
      }`}
    >
      {stepNumber !== undefined && (
        <div className="flex items-center gap-3 mb-4">
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold ${
              isDarkMode ? "bg-white text-black" : "bg-black text-white"
            }`}
          >
            {stepNumber}
          </div>
          <div className="flex-1">
            <h3
              className={`text-sm font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {node.text}
            </h3>
            {node.hint && (
              <p
                className={`text-xs mt-1 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                {node.hint}
              </p>
            )}
            <ClinicalLinkChips
              links={node.clinicalLinks}
              isDarkMode={isDarkMode}
            />
          </div>
        </div>
      )}

      {stepNumber === undefined && (
        <div className="mb-4">
          <h3
            className={`text-sm font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {node.text}
          </h3>
          {node.hint && (
            <p
              className={`text-xs mt-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              {node.hint}
            </p>
          )}
          <ClinicalLinkChips
            links={node.clinicalLinks}
            isDarkMode={isDarkMode}
          />
        </div>
      )}

      {preface && (
        <p
          data-testid="single-select-preface"
          className={`text-sm mb-3 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
        >
          {preface}
        </p>
      )}

      <RadioGroup
        value={selected || ""}
        onValueChange={(v) => setSelected(v)}
      >
        <div className="space-y-2">
          {node.options.map((opt) => (
            <div
              key={opt.id}
              className={`border rounded-lg p-3 cursor-pointer transition ${
                selected === opt.id
                  ? isDarkMode
                    ? "border-blue-500 bg-blue-950"
                    : "border-blue-500 bg-blue-50"
                  : isDarkMode
                    ? "border-gray-600 hover:bg-gray-700"
                    : "border-gray-200 hover:bg-gray-50"
              }`}
              onClick={() => setSelected(opt.id)}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem
                  value={opt.id}
                  id={`${node.id}-${opt.id}`}
                  aria-describedby={opt.hint ? `${node.id}-${opt.id}-hint` : undefined}
                />
                <div className="flex-1">
                  <label
                    htmlFor={`${node.id}-${opt.id}`}
                    className={`block text-sm cursor-pointer ${
                      isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                  >
                    {opt.label}
                  </label>
                  {opt.hint && (
                    <p
                      id={`${node.id}-${opt.id}-hint`}
                      className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                    >
                      {opt.hint}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </RadioGroup>

      <FlowPrimaryButton
        label="Continue"
        wrapperClassName="mt-4 flex justify-end"
        onClick={() => selected && onAnswer(selected)}
        disabled={!canContinue || !selected}
        blockedReason="Select an option to continue."
      />
    </div>
  );
}
